import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { environment } from '../../../enviroment';
import { ApiResponse } from '../../core/models/api.models';
import {
  AttachmentInput,
  ChatAttachment,
  ChatConversation,
  ChatMember,
  ChatMessage,
  ChatReaction,
  ChatSearchHit,
  ChatUserOption,
  UserPresence
} from './chat.models';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly base = 'v1/chat';

  listConversations(search?: string): Observable<ChatConversation[]> {
    return this.api.get<ChatConversation[]>(`${this.base}/conversations`, search ? { search } : undefined);
  }

  getConversation(id: string): Observable<ChatConversation> {
    return this.api.get<ChatConversation>(`${this.base}/conversations/${id}`);
  }

  startDirect(peerUserId: string): Observable<ChatConversation> {
    return this.api.post<ChatConversation>(`${this.base}/direct`, { peerUserId });
  }

  createGroup(body: {
    name: string;
    description?: string;
    avatarUrl?: string;
    projectId?: string;
    memberUserIds?: string[];
  }): Observable<ChatConversation> {
    return this.api.post<ChatConversation>(`${this.base}/groups`, body);
  }

  updateGroup(id: string, body: { name: string; description?: string; avatarUrl?: string }): Observable<ChatConversation> {
    return this.api.put<ChatConversation>(`${this.base}/groups/${id}`, body);
  }

  deleteGroup(id: string): Observable<unknown> {
    return this.api.delete(`${this.base}/groups/${id}`);
  }

  leaveGroup(id: string): Observable<unknown> {
    return this.api.post(`${this.base}/groups/${id}/leave`, {});
  }

  addMembers(id: string, userIds: string[]): Observable<unknown> {
    return this.api.post(`${this.base}/groups/${id}/members`, { userIds });
  }

  removeMember(id: string, userId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/groups/${id}/members/${userId}`);
  }

  setAdmin(id: string, userId: string, isAdmin: boolean): Observable<unknown> {
    return this.api.post(`${this.base}/groups/${id}/admins`, { userId, isAdmin });
  }

  transferOwnership(id: string, newOwnerUserId: string): Observable<unknown> {
    return this.api.post(`${this.base}/groups/${id}/transfer-ownership`, { newOwnerUserId });
  }

  listMembers(id: string): Observable<ChatMember[]> {
    return this.api.get<ChatMember[]>(`${this.base}/conversations/${id}/members`);
  }

  listMessages(id: string, take = 40, before?: string): Observable<ChatMessage[]> {
    const params: Record<string, string | number> = { take };
    if (before) params['before'] = before;
    return this.api.get<ChatMessage[]>(`${this.base}/conversations/${id}/messages`, params);
  }

  sendMessage(
    id: string,
    body: {
      content?: string;
      messageType?: string;
      replyToMessageId?: string;
      mentionsJson?: string;
      attachments?: AttachmentInput[];
    }
  ): Observable<ChatMessage> {
    return this.api.post<ChatMessage>(`${this.base}/conversations/${id}/messages`, body);
  }

  editMessage(id: string, messageId: string, content: string): Observable<ChatMessage> {
    return this.api.put<ChatMessage>(`${this.base}/conversations/${id}/messages/${messageId}`, { content });
  }

  deleteForMe(id: string, messageId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/conversations/${id}/messages/${messageId}/for-me`);
  }

  deleteForEveryone(id: string, messageId: string): Observable<unknown> {
    return this.api.delete(`${this.base}/conversations/${id}/messages/${messageId}/for-everyone`);
  }

  forwardMessage(targetRoomId: string, sourceMessageId: string): Observable<ChatMessage> {
    return this.api.post<ChatMessage>(`${this.base}/conversations/${targetRoomId}/messages/${sourceMessageId}/forward`, {});
  }

  pinMessage(id: string, messageId: string, pinned = true): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/messages/${messageId}/pin?pinned=${pinned}`, {});
  }

  listPinned(id: string): Observable<ChatMessage[]> {
    return this.api.get<ChatMessage[]>(`${this.base}/conversations/${id}/pinned`);
  }

  toggleReaction(id: string, messageId: string, emoji: string): Observable<ChatReaction[]> {
    return this.api.post<ChatReaction[]>(`${this.base}/conversations/${id}/messages/${messageId}/reactions`, { emoji });
  }

  pinConversation(id: string, pinned = true): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/pin?pinned=${pinned}`, {});
  }

  muteConversation(id: string, muted = true): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/mute?muted=${muted}`, {});
  }

  archiveConversation(id: string, archived = true): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/archive?archived=${archived}`, {});
  }

  markRead(id: string, messageId?: string): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/read`, { messageId: messageId ?? null });
  }

  notifyTyping(id: string, isTyping: boolean): Observable<unknown> {
    return this.api.post(`${this.base}/conversations/${id}/typing?isTyping=${isTyping}`, {});
  }

  sharedFiles(id: string, imagesOnly = false): Observable<ChatAttachment[]> {
    return this.api.get<ChatAttachment[]>(`${this.base}/conversations/${id}/files`, { imagesOnly });
  }

  search(q: string, roomId?: string, take = 30): Observable<ChatSearchHit[]> {
    const params: Record<string, string | number> = { q, take };
    if (roomId) params['roomId'] = roomId;
    return this.api.get<ChatSearchHit[]>(`${this.base}/search`, params);
  }

  updatePresence(status: number, statusMessage?: string): Observable<UserPresence> {
    return this.api.post<UserPresence>(`${this.base}/presence`, { status, statusMessage });
  }

  listPresence(userIds?: string[]): Observable<UserPresence[]> {
    return this.api.get<UserPresence[]>(`${this.base}/presence`, userIds?.length ? { userIds: userIds.join(',') } : undefined);
  }

  ensureProjectDiscussion(projectId: string): Observable<ChatConversation> {
    return this.api.post<ChatConversation>(`${this.base}/projects/${projectId}/discussion`, {});
  }

  upload(files: File[]): Observable<AttachmentInput[]> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f, f.name));
    return this.http
      .post<ApiResponse<AttachmentInput[]>>(`${environment.apiUrl}/${this.base}/upload`, form)
      .pipe(map((res) => {
        if (!res?.success) throw new Error(res?.message || 'Upload failed.');
        return res.data ?? [];
      }));
  }

  listUsers(search?: string): Observable<ChatUserOption[]> {
    return this.api.get<ChatUserOption[]>(`${this.base}/directory`, search ? { search, take: 40 } : { take: 40 });
  }

  fileUrl(url?: string | null): string {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const origin = (environment.apiOrigin || '').replace(/\/$/, '');
    return `${origin}${url.startsWith('/') ? url : '/' + url}`;
  }
}
